import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';

/**
 * Read from the local mirror, not the network. Every list in the app is a
 * live Dexie query, so a screen keeps working when the signal drops and
 * updates itself the moment a sync brings new rows in.
 */

/**
 * Dexie throws when asked for a table it does not have, and a hook cannot
 * be called conditionally — so a form field with no foreign key still has
 * to call the lookup hook with something. Returning an empty result for an
 * unknown table is what lets that be safe.
 */
function known(table: string): boolean {
  return db.tables.some((t) => t.name === table);
}

export function useRows<T = Record<string, unknown>>(
  table: string,
  orderBy?: string,
): T[] | undefined {
  return useLiveQuery(async () => {
    if (!known(table)) return [] as T[];
    const rows = (await db.table(table).toArray()) as T[];
    const live = rows.filter((r) => !(r as { deleted_at?: string | null }).deleted_at);
    if (!orderBy) return live;
    return live.sort((a, b) => {
      const av = (a as Record<string, unknown>)[orderBy];
      const bv = (b as Record<string, unknown>)[orderBy];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv), 'en-ZA', { numeric: true });
    });
  }, [table, orderBy]);
}

export function useRow<T = Record<string, unknown>>(
  table: string,
  id: string | undefined,
): T | undefined {
  return useLiveQuery(
    async () =>
      id && known(table) ? ((await db.table(table).get(id)) as T | undefined) : undefined,
    [table, id],
  );
}

/** id -> display label, for rendering foreign keys in tables. */
export function useLookup(table: string, labelField: string): Map<string, string> {
  const rows = useRows(table);
  const map = new Map<string, string>();
  for (const r of rows ?? []) {
    const rec = r as Record<string, unknown>;
    map.set(String(rec.id), String(rec[labelField] ?? ''));
  }
  return map;
}

/**
 * Several foreign-key lookups in one hook. A hook cannot be called in a
 * loop, so a table with two or more FK columns needs this rather than
 * repeated useLookup calls.
 */
export function useLookups(
  defs: { table: string; label: string }[],
): Record<string, Map<string, string>> {
  const key = defs.map((d) => `${d.table}:${d.label}`).join('|');

  return (
    useLiveQuery(async () => {
      const out: Record<string, Map<string, string>> = {};
      for (const d of defs) {
        if (!known(d.table)) { out[d.table] = new Map(); continue; }
        const rows = (await db.table(d.table).toArray()) as Record<string, unknown>[];
        const map = new Map<string, string>();
        for (const r of rows) map.set(String(r.id), String(r[d.label] ?? r.code ?? ''));
        out[d.table] = map;
      }
      return out;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]) ?? {}
  );
}
